import type { InventoryInformationEditView, VehicleType } from "@slfinrtl/ui-bff-routes"
import { useMemo } from "react"
import { defineMessages } from "react-intl"
import type { RadioOption } from "src/components/form/radio/radioGroup/RadioGroup"
import { useMinimumSubsidizedUsageTime } from "../useMinimumSubsidizedUsageTime"
import { useInStockOption } from "./useInStockOption"
import { useInUseOption } from "./useInUseOption"
import { useIncomingOption } from "./useIncomingOption"

export const stockStatusOptionMessages = defineMessages({
	inUse: {
		defaultMessage: "In Use ({dayCount, plural, =0 {today} one {# day} other {# days}})",
		id: "vehicle.stockStatus.inUseSince",
	},
	minimumSubsidizedUsageTime: {
		defaultMessage: "Minimum in service time: {dayCount} days",
		id: "inventoryAndIntake.inventory.stockStatus.minimumUsageTime",
	},
	notPossible: {
		defaultMessage: "Cannot be set for selected vehicle type: {vehicleType}",
		id: "inventoryAndIntake.inventory.stockStatus.selectionNotPossible",
	},
	takeOutOfService: {
		defaultMessage: "Take out of service",
		id: "inventoryAndIntake.inventory.stockStatus.takeOutOfService",
	},
	vehicleAlreadyInStock: {
		defaultMessage: "The vehicle was already in stock and can't be set back to future intake.",
		id: "inventoryAndIntake.inventory.stockStatus.vehicleAlreadyInStock",
	},
})

export const useStockStatusOptions = (
	selectedVehicleType: VehicleType | undefined,
	vmsDataSource: "ONE_VMS" | "PVMS" | undefined,
	isDraft: boolean,
	view: InventoryInformationEditView,
): RadioOption[] => {
	const { hasMinimumSubsidizedUsageTime, subsidizedUsageCannotBeTerminated } = useMinimumSubsidizedUsageTime(view)

	const incomingOption = useIncomingOption(
		vmsDataSource,
		view.values.inventoryAndIntake.stockStatus === "INCOMING",
		isDraft,
	)
	const inStockOption = useInStockOption(vmsDataSource, subsidizedUsageCannotBeTerminated, view.subsidized)
	const inUseOption = useInUseOption(
		vmsDataSource,
		selectedVehicleType,
		hasMinimumSubsidizedUsageTime,
		view.usageTime,
	)

	return useMemo(() => [incomingOption, inStockOption, inUseOption], [incomingOption, inStockOption, inUseOption])
}
